/**
 * Module-aware dashboard routing for multi-service web app.
 * Used by logo navigation, Home module picker, and route guards.
 */

const MODULE_PREFIX_ROUTES = [
  { prefix: '/sleep', dashboard: '/sleep/dashboard' },
  { prefix: '/dme', dashboard: '/dme/dashboard' },
  { prefix: '/dmp', dashboard: '/dmp/dashboard' },
  { prefix: '/marketing', dashboard: '/marketing/dashboard' },
  { prefix: '/doc-management', dashboard: '/doc-management/dashboard' },
  { prefix: '/staffing', dashboard: '/staffing/dashboard' },
  { prefix: '/it', dashboard: '/it/dashboard' },
  { prefix: '/admin-console', dashboard: '/admin-console/dashboard' },
  { prefix: '/home', dashboard: '/home' },
];

const normalizeServiceKey = (serviceKey) => {
  if (serviceKey == null) return '';
  if (typeof serviceKey === 'object') {
    return String(serviceKey.service_key || serviceKey.name || '').toLowerCase();
  }
  return String(serviceKey).toLowerCase();
};

/**
 * Map a service key (from API or localStorage) to its dashboard path.
 */
export const serviceKeyToDashboardRoute = (serviceKey) => {
  const key = normalizeServiceKey(serviceKey);
  if (!key) return '/home';
  if (key.includes('sleep') || key === '2') return '/sleep/dashboard';
  if (key.includes('dme') || key === '3') return '/dme/dashboard';
  if (key.includes('dmp')) return '/dmp/dashboard';
  if (key.includes('document')) return '/doc-management/dashboard';
  if (key.includes('marketing')) return '/marketing/dashboard';
  if (key.includes('staffing')) return '/staffing/dashboard';
  if (key.includes('it') || key.includes('security')) return '/it/dashboard';
  if (key.includes('admin')) return '/admin-console/dashboard';
  if (key.includes('restore') || key === '1') return '/dashboard';
  return '/home';
};

/**
 * Dashboard path for the module implied by the current URL.
 */
export const getModuleDashboardPath = (pathname, { fallback } = {}) => {
  const path = pathname || '/';

  for (const { prefix, dashboard } of MODULE_PREFIX_ROUTES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return dashboard;
    }
  }

  if (path === '/dashboard' || path.startsWith('/dashboard/')) {
    return '/dashboard';
  }

  if (path.startsWith('/restore')) {
    return '/dashboard';
  }

  const fromStorage = getDashboardFromUserServiceAccess();
  if (fromStorage) return fromStorage;

  return fallback ?? '/home';
};

/**
 * First assigned service dashboard from localStorage (post-login / module picker).
 */
export const getDashboardFromUserServiceAccess = () => {
  try {
    const access = JSON.parse(localStorage.getItem('userServiceAccess') || '{}');
    const services = access.services;
    if (!Array.isArray(services) || services.length === 0) return null;
    return serviceKeyToDashboardRoute(services[0]);
  } catch {
    return null;
  }
};

export const serviceIndicatesRestore = (service) => {
  const key = normalizeServiceKey(service);
  return key.includes('restore') || key === '1';
};

/**
 * Whether the user may access Restore (/dashboard) from localStorage.
 * @returns {boolean|null} true/false, or null if unknown (no services in storage)
 */
export const getRestoreAccessFromStorage = () => {
  try {
    const access = JSON.parse(localStorage.getItem('userServiceAccess') || '{}');
    const services = access.services;
    if (!Array.isArray(services) || services.length === 0) return null;
    return services.some(serviceIndicatesRestore);
  } catch {
    return null;
  }
};

/**
 * Whether the user has Restore access according to getUserServices() API list.
 */
export const userHasRestoreAccessFromServices = (services) => {
  if (!Array.isArray(services)) return false;
  return services.some(serviceIndicatesRestore);
};

/**
 * Where to send a user who cannot access Restore.
 */
export const getRedirectForNonRestoreUser = (services) => {
  if (!Array.isArray(services) || services.length === 0) {
    return '/no-access';
  }
  if (services.length === 1) {
    return serviceKeyToDashboardRoute(services[0]);
  }
  return '/home';
};
